import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { FaPlus, FaEdit, FaTrash, FaFileExcel, FaCircle, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { getData, postData } from "../../services/FetchNodeservices";

dayjs.extend(customParseFormat);

export default function SubmissionReport() {
  const allowedUserIds = [59, 26, 36, 19, 22, 16, 39, 63, 48, 54, 5, 21, 44, 38, 77, 69, 27, 23, 130, 133];
  
  const userObj = (() => {
    try {
      return JSON.parse(localStorage.getItem("USER") || "{}");
    } catch {
      return {};
    }
  })();
  
  const userId = Number(localStorage.getItem("user_id")) || Number(userObj?.id);
  const userRole = userObj?.designation_name || "";
  const isLeadOrManager = ["Director", "Vice President", "VP", "Manager", "Team Lead", "Senior HR", "Account Manager"].includes(userRole);
  const isAdmin = String(localStorage.getItem("is_admin")).toLowerCase() === "true" || !!localStorage.getItem("ADMIN_INFO");

  const [canAssign, setCanAssign] = useState(false);
  const canEditDelete = canAssign || isLeadOrManager || isAdmin || allowedUserIds.includes(userId);

  const TODAY_STR = dayjs().format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    date: "",
    customFrom: "",
    customTo: "",
    company: "",
    location: "",
    team: [],
    teamSearch: "",
  });

  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const [formValues, setFormValues] = useState({
    date: TODAY_STR,
    due_date: "",
    employeename: [],
    profile: "", 
    task_description: "",
    company: "",
    location: "",
    priority: "Medium",
    target_cv: 0,
    cv_sourced: 0,
    status: "Pending",
    extension_reason: ""
  });

  const [originalDueDate, setOriginalDueDate] = useState("");

  useEffect(() => {
    document.body.classList.toggle("modal-open", openForm);
  }, [openForm]);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".team-dropdown")) setTeamDropdownOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const loadData = async () => {
    const teamResp = await getData(`workingtask/team?user_id=${userId}`);
    if (teamResp?.status) {
      setTeamMembers(teamResp.data || []);
      if (teamResp.canAssign !== undefined) {
        setCanAssign(Boolean(teamResp.canAssign));
      } else if (teamResp.data?.length > 1) {
        setCanAssign(true);
      }
    }

    const subs = await postData("workingtask/fetch", { user_id: userId });
    if (subs.status) {
      setData(
        subs.data.map((r) => ({
          ...r,
          employeename: r.employeename 
            ? (Array.isArray(r.employeename) ? r.employeename : String(r.employeename).split(",").map((x) => x.trim()))
            : [],
        }))
      );
    }

    const emp = await getData("workingtask/employers");
    if (emp.status) setEmployees(emp.data);

    const comp = await getData("workingtask/companies");
    if (comp.status) setCompanies(comp.data);
  };

  useEffect(() => {
    loadData();
  }, [userId]);

  const filteredData = useMemo(() => {
    const selectedTeamNames = teamMembers
      .filter((m) => filters.team.includes(String(m.id)))
      .map((x) => x.name);

    return data.filter((r) => {
      if (filters.company && r.company !== filters.company) return false;
      if (filters.location && !r.location?.toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (filters.team.length > 0) {
        const hasMatch = r.employeename.some((e) => selectedTeamNames.includes(e));
        if (!hasMatch) return false;
      }
      if (filters.date) {
        const rowDate = dayjs(r.date);
        const today = dayjs().startOf("day");
        if (!rowDate.isValid()) return false;
        switch (filters.date) {
          case "today": if (!rowDate.isSame(today, "day")) return false; break;
          case "custom":
            if (filters.customFrom && rowDate.isBefore(dayjs(filters.customFrom), "day")) return false;
            if (filters.customTo && rowDate.isAfter(dayjs(filters.customTo), "day")) return false;
            break;
          default: break;
        }
      }
      return true;
    });
  }, [data, filters, teamMembers]);

  const getDisplayStatus = (row) => {
    const today = dayjs().startOf('day');
    const due = dayjs(row.due_date);
    if (row.status !== 'Completed' && due.isValid() && today.isAfter(due, 'day')) {
      return "Expired";
    }
    return row.status;
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed': return "bg-green-100 text-green-700";
      case 'In Progress': return "bg-blue-100 text-blue-700";
      case 'Expired': return "bg-red-100 text-red-700 font-bold";
      case 'On Hold': return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const openFormPopup = (index = null) => {
    if (index !== null) {
      const r = filteredData[index];
      setEditingIndex(index);
      setOriginalDueDate(r.due_date);
      setFormValues({
        ...r,
        date: dayjs(r.date).format("YYYY-MM-DD"),
        due_date: r.due_date ? dayjs(r.due_date).format("YYYY-MM-DD") : "",
        employeename: Array.isArray(r.employeename) ? r.employeename : []
      });
    } else {
      setEditingIndex(null);
      setOriginalDueDate("");
      setFormValues({
        date: TODAY_STR,
        due_date: dayjs().add(7, 'day').format("YYYY-MM-DD"),
        employeename: [],
        profile: "",
        task_description: "",
        company: "",
        location: "",
        priority: "Medium",
        target_cv: 0,
        cv_sourced: 0,
        status: "Pending",
        extension_reason: ""
      });
    }
    setOpenForm(true);
  };

const saveForm = async () => {
    // Lead validation
    if (canEditDelete && editingIndex !== null && formValues.due_date !== originalDueDate && !formValues.extension_reason) {
        alert("Please provide a reason for extending the deadline.");
        return;
    }

    const payload = { ...formValues, user_id: userId };
    const record = editingIndex !== null ? filteredData[editingIndex] : null;

    if (editingIndex !== null) {
      // THE FIX IS HERE:
      if (canEditDelete) {
        // If user is a LEAD, call the protected update route
        await postData(`workingtask/update/${record.id}`, payload);
      } else {
        // If user is a RECRUITER, call the progress-only route
        // This route does NOT have checkPermission in backend
        await postData(`workingtask/update-cv/${record.id}`, {
          user_id: userId,
          cv_sourced: formValues.cv_sourced,  
          location: formValues.location
        });
      }
    } else {
      // Add new task (Only leads can see this button)
      await postData("workingtask/add", payload);
    }

    loadData();
    setOpenForm(false);
};

  const exportExcel = () => {
    const rows = filteredData.map((r) => ({
      AssignDate: r.date,
      DueDate: r.due_date,
      Task: r.profile,
      Employees: r.employeename.join(", "),
      Priority: r.priority,
      Status: getDisplayStatus(r),
      TargetCV: r.target_cv,
      SourcedCV: r.cv_sourced,
      Location: r.location
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks");
    XLSX.writeFile(wb, "Task_Report.xlsx");
  };

  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Task Management System
          </h1>
          <p className="text-sm font-semibold text-gray-400 mt-1">
            Allocate recruitment targets, track candidate submission velocity, and manage client assignments
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEditDelete && (
            <button 
              onClick={() => openFormPopup()} 
              className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-white font-extrabold bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 text-xs"
            >
              <FaPlus /> New Task
            </button>
          )}

          <button 
            onClick={exportExcel} 
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white border border-gray-200 text-gray-700 font-extrabold shadow-sm hover:bg-gray-50 transition duration-200 text-xs"
          >
            <FaFileExcel className="text-emerald-600" /> Export Excel
          </button>
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Filter by Date</label>
            <select 
              className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
              value={filters.date} 
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            >
              <option value="">All Dates</option>
              <option value="today">Today</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {filters.date === "custom" && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <input 
                  type="date" 
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                  value={filters.customFrom} 
                  onChange={(e) => setFilters({ ...filters, customFrom: e.target.value })} 
                />
              </div>
              <div className="flex-1">
                <input 
                  type="date" 
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                  value={filters.customTo} 
                  onChange={(e) => setFilters({ ...filters, customTo: e.target.value })} 
                />
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Filter by Client</label>
            <select 
              className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
              value={filters.company} 
              onChange={(e) => setFilters({ ...filters, company: e.target.value })}
            >
              <option value="">All Companies</option>
              {companies.map((c, i) => <option key={i} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="relative team-dropdown">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Filter by Team Member</label>
            <div className="relative">
              <input 
                type="text" 
                placeholder="Type member name..." 
                className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium" 
                value={filters.teamSearch} 
                onFocus={() => setTeamDropdownOpen(true)} 
                onChange={(e) => setFilters({ ...filters, teamSearch: e.target.value })} 
              />
            </div>
            {teamDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setTeamDropdownOpen(false)} />
                <div className="absolute bg-white shadow-xl border border-gray-150 w-full rounded-2xl mt-1.5 max-h-48 overflow-y-auto z-20 p-3 space-y-1.5 no-scrollbar">
                  {teamMembers.filter(m => m.name.toLowerCase().includes(filters.teamSearch.toLowerCase())).map((m) => (
                    <label key={m.id} className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-[#f4f6fa]/50 rounded-xl cursor-pointer text-xs font-semibold text-gray-700 hover:text-gray-900 transition">
                      <input 
                        type="checkbox" 
                        className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                        checked={filters.team.includes(String(m.id))} 
                        onChange={() => {
                          const idStr = String(m.id);
                          setFilters(prev => ({ ...prev, team: prev.team.includes(idStr) ? prev.team.filter(t => t !== idStr) : [...prev.team, idStr] }));
                        }} 
                      /> 
                      <span>{m.name}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-indigo-100/20 overflow-hidden">
        <div className="w-full overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="w-full border-collapse text-left text-sm text-gray-750">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-left">
                <th className="px-6 py-3.5">Assign / Due Date</th>
                <th className="px-6 py-3.5">Task & Client</th>
                <th className="px-6 py-3.5">Assigned To</th>
                <th className="px-6 py-3.5 text-center">Priority</th>
                <th className="px-6 py-3.5 text-center">CV Sourced / Target</th>
                <th className="px-6 py-3.5 text-center">Status</th>
                <th className="px-6 py-3.5 text-right w-[110px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredData.length > 0 ? (
                filteredData.map((row, index) => {
                  const displayStatus = getDisplayStatus(row);
                  return (
                    <tr key={row.id} className="hover:bg-gray-50/30 transition text-gray-700">
                      <td className="px-6 py-4">
                        <div className="font-bold text-gray-900">{dayjs(row.date).format("DD MMM YYYY")}</div>
                        <div className="text-[10px] text-red-500 font-extrabold flex items-center gap-1 mt-1">
                          <FaCalendarAlt size={9}/> Due: {row.due_date ? dayjs(row.due_date).format("DD MMM YYYY") : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-black text-indigo-950 text-sm">{row.profile}</div>
                        <div className="text-[10px] font-extrabold text-gray-400 uppercase flex items-center gap-1.5 mt-0.5">
                          <span>{row.company}</span>
                          <span>|</span>
                          <span className="flex items-center gap-0.5"><FaMapMarkerAlt size={9} className="text-gray-400"/> {row.location || 'Remote'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {row.employeename.map((emp, i) => (
                            <span 
                              key={i} 
                              className="bg-indigo-50 border border-indigo-100/50 text-indigo-650 px-2 py-0.5 rounded-full text-[10px] font-bold"
                            >
                              {emp}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-black ${
                          row.priority === 'High' ? 'text-red-650' : row.priority === 'Medium' ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          <FaCircle size={6} /> {row.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-extrabold text-xs">
                        <span className="text-indigo-600 font-black">{row.cv_sourced || 0}</span>
                        <span className="text-gray-300 mx-1">/</span>
                        <span className="text-gray-400 font-bold">{row.target_cv || 0}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase border tracking-wider inline-block ${
                          displayStatus === 'Completed'
                            ? "bg-green-50 text-green-600 border-green-100"
                            : displayStatus === 'In Progress'
                            ? "bg-blue-50 text-blue-600 border-blue-100"
                            : displayStatus === 'On Hold'
                            ? "bg-amber-50 text-amber-600 border-amber-100"
                            : "bg-red-50 text-red-600 border-red-100"
                        }`}>
                          {displayStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            className="p-1.5 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition"
                            onClick={() => openFormPopup(index)}
                          >
                            <FaEdit className="w-3.5 h-3.5" />
                          </button>
                          {canEditDelete && (
                            <button
                              className="p-1.5 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition"
                              onClick={async () => {
                                if(window.confirm("Delete this task?")) {
                                  await postData(`workingtask/delete/${row.id}`, { user_id: userId });
                                  loadData();
                                }
                              }}
                            >
                              <FaTrash className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-400 font-semibold">
                    No task assignments found matching criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL */}
      {openForm && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] p-8 w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl border border-gray-100 no-scrollbar">
            <div className="flex justify-between items-center mb-6 border-b border-gray-50 pb-4">
              <h2 className="text-xl font-black text-gray-900">
                {editingIndex !== null ? "Update Task Details" : "Create New Assignment"}
              </h2>
              <button 
                onClick={() => setOpenForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* FIELDS RESTRICTED TO LEADS */}
              {canEditDelete ? (
                <>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Task / Profile Name</label>
                    <input 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.profile} 
                      onChange={(e) => setFormValues({ ...formValues, profile: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Company / Client</label>
                    <select 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.company} 
                      onChange={(e) => setFormValues({ ...formValues, company: e.target.value })}
                    >
                      <option value="">Select Client</option>
                      {companies.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Task Description (Optional)</label>
                    <textarea 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold h-16" 
                      rows="2" 
                      value={formValues.task_description} 
                      onChange={(e) => setFormValues({ ...formValues, task_description: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assign Date</label>
                    <input 
                      type="date" 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.date} 
                      onChange={(e) => setFormValues({ ...formValues, date: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Due Date (Deadline)</label>
                    <input 
                      type="date" 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.due_date} 
                      onChange={(e) => setFormValues({ ...formValues, due_date: e.target.value })} 
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Priority</label>
                    <select 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.priority} 
                      onChange={(e) => setFormValues({ ...formValues, priority: e.target.value })}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Current Status</label>
                    <select 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.status} 
                      onChange={(e) => setFormValues({ ...formValues, status: e.target.value })}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Target CV Count</label>
                    <input 
                      type="number" 
                      className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                      value={formValues.target_cv} 
                      onChange={(e) => setFormValues({ ...formValues, target_cv: e.target.value })} 
                    />
                  </div>
                </>
              ) : (
                <div className="col-span-1 md:col-span-2 bg-indigo-50 border border-indigo-100 p-4 rounded-2xl text-indigo-750 text-xs font-semibold">
                  ℹ️ Note: As a Recruiter, you are authorized to update CV Sourced counts and job locations.
                </div>
              )}

              {/* FIELDS EDITABLE BY EVERYONE */}
              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">CVs Sourced So Far</label>
                <input 
                  type="number" 
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-semibold" 
                  value={formValues.cv_sourced} 
                  onChange={(e) => setFormValues({ ...formValues, cv_sourced: e.target.value })} 
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Job Location</label>
                <input 
                  className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium" 
                  placeholder="e.g. Noida, Remote" 
                  value={formValues.location} 
                  onChange={(e) => setFormValues({ ...formValues, location: e.target.value })} 
                />
              </div>

              {/* MULTISELECT (LEADS ONLY) */}
              {canEditDelete && (
                <div className="col-span-1 md:col-span-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5">Assign to Employees</label>
                  <div className="border border-gray-100 bg-[#f4f6fa]/30 rounded-2xl p-4 max-h-40 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-2.5 no-scrollbar">
                    {teamMembers.map((emp) => (
                      <label key={emp.id} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-white rounded-xl cursor-pointer text-xs font-semibold text-gray-700 hover:text-gray-900 border border-transparent hover:border-gray-100 hover:shadow-sm transition bg-[#f4f6fa]/40">
                        <input 
                          type="checkbox" 
                          className="rounded text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5"
                          checked={formValues.employeename.includes(emp.name)}
                          onChange={() => setFormValues(prev => ({
                            ...prev,
                            employeename: prev.employeename.includes(emp.name)
                              ? prev.employeename.filter(x => x !== emp.name)
                              : [...prev.employeename, emp.name]
                          }))}
                        /> 
                        <span>{emp.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* EXTENSION REASON */}
              {canEditDelete && editingIndex !== null && formValues.due_date !== originalDueDate && (
                <div className="col-span-1 md:col-span-2 bg-red-50/50 p-4 rounded-2xl border border-red-100 space-y-2">
                  <label className="text-[10px] font-bold text-red-700 uppercase tracking-wider block">Reason for extending deadline *</label>
                  <input 
                    className="w-full px-4 py-2.5 rounded-2xl bg-white text-gray-800 border border-red-200 focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition duration-200 text-sm font-semibold" 
                    placeholder="Enter reason details..." 
                    value={formValues.extension_reason} 
                    onChange={(e) => setFormValues({ ...formValues, extension_reason: e.target.value })} 
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-50">
              <button 
                onClick={() => setOpenForm(false)} 
                className="bg-[#f4f6fa] text-gray-700 font-bold px-6 py-2.5 rounded-2xl hover:bg-gray-100 transition text-sm"
              >
                Cancel
              </button>
              <button 
                onClick={saveForm} 
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-2.5 rounded-2xl shadow-lg shadow-indigo-100 transition text-sm"
              >
                {editingIndex !== null ? "Update Task" : "Save Task"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`body.modal-open { overflow: hidden; }`}</style>
    </div>
  );
}